import React,
{
	useEffect,
	useMemo,
	useState
} from "react";
import MuiTheme
	from "@components/providers/MuiTheme";
import {
    AdapterDayjs
}
    from "@mui/x-date-pickers-pro/AdapterDayjs";
import {
    LocalizationProvider
} from "@mui/x-date-pickers-pro";
import {
    DrawerProvider
} from "@components/providers/DrawerProvider";
import CompanyProfileProvider
	from "@components/providers/CompanyProfileProvider";
import 'dayjs/locale/en-gb';
import styled
    from 'styled-components';
import { fetchWhoAmI } from "@network/session";
import {
	QueryClient,
	QueryClientProvider
} from "@tanstack/react-query";
import { defaultTanstackConfig } from "@/config/network";

const queryClient = new QueryClient({
	defaultOptions: {
		queries: {
			...defaultTanstackConfig.queries
		},
	},
})

const ThemeWrapper = styled.div`
  & {
    font-family: 'Outfit', sans-serif;
  }
`;

export const SESSION_STORAGE_KEY = "company-data-cache-23";

let __localCompanyDataCache = null;
let activeFetch = null;

const storeLocalCache = (data) => {
	sessionStorage.setItem(SESSION_STORAGE_KEY, btoa(encodeURIComponent(JSON.stringify({
		expires: Date.now() + 1000 * 60, // 1 minute to save the world. ~ Madonna.
		payload: data
	}))));
}

const getLocalCache = () => {
	let item = sessionStorage.getItem(SESSION_STORAGE_KEY);
	if(item) {
		try {
			item = JSON.parse(decodeURIComponent(atob(item)));
			if(item.expires > Date.now()) {
				return item.payload;
			}
		} catch(ex) {
			//swallow exception.
		}
	}

	return null;
}

const fetchCompanyData = ((force = false) => {
	if(!force && __localCompanyDataCache) {
		return Promise.resolve(__localCompanyDataCache);
	}
	if(!force && activeFetch) {
		return activeFetch;
	}

	return activeFetch = fetchWhoAmI().then(response => {
		return response.company;
	}).catch( () => {});
});

const parseLocalCompanyData = (companyData) => {
	if(typeof companyData === "string") {
		const result = JSON.parse(atob(companyData));
		if(result.private) return result;

		__localCompanyDataCache = JSON.parse(atob(companyData));
		return __localCompanyDataCache;
	}

	if(Object.keys(companyData).length == 0) {
		if(__localCompanyDataCache == null) {
			__localCompanyDataCache = getLocalCache();
		}

		if(__localCompanyDataCache)
			return __localCompanyDataCache;
	} else {
		if(getLocalCache() == null) storeLocalCache(companyData);
	}

	return companyData;

}

const Spa = ({
                 children,
                 as = "div",
				 companyData = {}
             }) => {

	const [parsedCompanyData, setParsedCompanyData] = useState(parseLocalCompanyData(companyData));

	const fetchAndSet = (force = false) => {
		fetchCompanyData(force).then(data => {
			setParsedCompanyData(data);
		});
	}

	useEffect(() => {
		try {
			if(parsedCompanyData == undefined || companyData == null || (typeof parsedCompanyData == 'object' && Object.keys(parsedCompanyData).length == 0)) {
				fetchAndSet();
			}
		} catch(ex) {
			//swallow exception.
		}
	}, [companyData, parsedCompanyData]);

	const asComponent = useMemo(() => {
		const ThemeComponent = typeof as === 'string' ? ThemeWrapper : React.Fragment;
		const ThemeComponentProps = typeof as === 'string' ? {as} : {};

		return {
			ThemeComponent,
			ThemeComponentProps
		}
	}, [as]);

	const {ThemeComponent, ThemeComponentProps} = asComponent

    return (
		<QueryClientProvider client={queryClient}>
			<CompanyProfileProvider onRefresh={fetchAndSet} data={parsedCompanyData}>
				<ThemeComponent {...ThemeComponentProps}>
					<MuiTheme>
						<LocalizationProvider
							dateAdapter={AdapterDayjs}
							adapterLocale="en-gb"
						>
							<DrawerProvider>
								{children}
							</DrawerProvider>
						</LocalizationProvider>
					</MuiTheme>
				</ThemeComponent>
			</CompanyProfileProvider>
		</QueryClientProvider>
		);
}

export default Spa;